// @ts-strict-ignore
/**
 * FX HedgePool Inc. Proprietary and Confidential
 *
 * This source code and the algorithms implemented herein are the sole property of FX HedgePool, Inc.,
 * are confidential information, and are trade secrets of FX HedgePool, Inc.
 * Redistribution or use of this source code or the algorithms implemented herein, in whole or in part,
 * without the express written permission of FX HedgePool, Inc. is strictly prohibited.
 */

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';

import { environment } from '../../environments/environment';

type HTTPParams = Record<string, string | number>;

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly originFrontend: string = environment.backendServer;
  private newOptions = {
    headers: new HttpHeaders(),
    params: new HttpParams(),
    body: {},
  };

  constructor(private readonly http: HttpClient) {}

  /**
   * As of 12/17/2020
   * @summary
   * Handles all GET requests
   * @param1 endpoint - This is the endpoint being requested
   * @param2 parameters - Option parameter doesn't need to be sent, will be set in the URL header
   */
  public get(
    endpoint: string,
    parameters?: HTTPParams,
    apiPath = this.originFrontend,
  ): Observable<any> {
    this.setHeaders();
    this.setParams(parameters);
    return this.http
      .get(`${apiPath}${endpoint}`, this.newOptions)
      .pipe(first());
  }

  public getAwait(
    endpoint: string,
    parameters?: HTTPParams,
    apiPath = this.originFrontend,
  ): Promise<any> {
    this.setHeaders();
    this.setParams(parameters);
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    return this.http.get(`${apiPath}${endpoint}`, this.newOptions).toPromise();
  }

  /**
   * As of 12/17/2020
   * @summary
   * Handles all Delete requests (Delete cannot be used so named deleteApi)
   * @param1 endpoint - This is the endpoint being requested
   */
  public deleteApi(
    endpoint: string,
    apiPath = this.originFrontend,
    body?: any,
  ): Observable<any> {
    this.setHeaders();
    const options = this.newOptions;
    options.body = body;
    return this.http.delete(`${apiPath}${endpoint}`, options).pipe(first());
  }

  /**
   * As of 12/17/2020
   *
   * @summary
   * Handles all PUT requests
   *
   * @param1 endpoint - This is the endpoint being requested
   * @param2 body - This is the body being sent
   * @param3 parameters - Option parameter doesn't need to be sent, will be set in the URL header
   */

  public put(
    endpoint: string,
    body: any,
    parameters?: HTTPParams,
    apiPath = this.originFrontend,
  ): Observable<any> {
    this.setHeaders();
    this.setParams(parameters);
    return this.http
      .put(`${apiPath}${endpoint}`, body, this.newOptions)
      .pipe(first());
  }

  public putAwait(
    endpoint: string,
    body: any,
    parameters?: HTTPParams,
    apiPath = this.originFrontend,
  ): Promise<any> {
    this.setHeaders();
    this.setParams(parameters);
    return (
      this.http
        .put(`${apiPath}${endpoint}`, body, this.newOptions)
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        .toPromise()
    );
  }
  /**
   * As of 12/17/2020
   *
   * @summary
   * Handles all POST requests
   *
   * @param1 endpoint - This is the endpoint being requested
   * @param2 body - This is the body being sent
   * @param3 parameters - Option parameter doesn't need to be sent, will be set in the URL header
   */

  public post(
    endpoint: string,
    body: any,
    parameters?: HTTPParams,
    apiPath = this.originFrontend,
  ): Observable<any> {
    this.setHeaders();
    this.setParams(parameters);
    return this.http
      .post(`${apiPath}${endpoint}`, body, this.newOptions)
      .pipe(first());
  }

  public postAwait(
    endpoint: string,
    body: any,
    parameters?: HTTPParams,
    apiPath = this.originFrontend,
  ): Promise<any> {
    this.setHeaders();
    this.setParams(parameters);

    return (
      this.http
        .post(`${apiPath}${endpoint}`, body, this.newOptions)
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        .toPromise()
    );
  }

  public postCall(
    endpoint: string,
    body: any,
    parameters?: HTTPParams,
  ): Observable<any> {
    this.setParams(parameters);
    return this.http.post(`${endpoint}`, body, this.newOptions).pipe(first());
  }

  /**
   * As of 12/17/2020
   *
   * @summary
   * Sets headers for all requests
   * Gets EntityType and MemberId (soon to be replaced with EntityId) from sessionStorage
   */

  public onSetHeaders(): void {
    let headers: HttpHeaders = new HttpHeaders({});

    if (sessionStorage.getItem('selectedEntityId')) {
      headers = headers.append(
        'MemberId',
        sessionStorage.getItem('selectedEntityId'),
      );
    }

    if (sessionStorage.getItem('entityType')) {
      headers = headers.append(
        'entityType',
        sessionStorage.getItem('entityType'),
      );
    }

    this.newOptions.headers = headers;
  }

  public setHeaders(): void {
    this.onSetHeaders();
  }

  /**
   * As of 12/17/2020
   *
   * @summary
   * Deletes headers, gets called on logout
   */
  public destroyHeaders(): void {
    this.newOptions = {
      headers: new HttpHeaders(),
      params: new HttpParams(),
      body: {},
    };
  }

  /**
   * As of 12/17/2020
   *
   * @summary
   * Should be moved to admin service
   * Gets current build version and when app was last updated
   */

  public getCurrentVersion(): Observable<any> {
    return this.http.get(
      `https://app-meta.fxhedgepool.com?env=${environment.name}`,
    );
  }

  /**
   * As of 12/17/2020
   *
   * @summary
   * Used when forcing user to reload page to clear their cache
   */

  public reload(): void {
    location.reload();
  }

  /**
   * As of 12/17/2020 Admin only
   *
   * @summary
   * Potentitally a duplicate form Set Headers
   * Used for Admins to set the memberId and entityType in headers
   */

  public setAdminAsMemberHeaders(member: number, partyType: string): void {
    let headers: HttpHeaders = new HttpHeaders({});
    headers = headers.append('MemberId', member.toString());
    if (partyType) {
      headers = headers.append('entityType', partyType);
    } else if (sessionStorage.getItem('entityType')) {
      headers = headers.append(
        'entityType',
        sessionStorage.getItem('entityType'),
      );
    }

    this.newOptions = { headers: headers, params: new HttpParams(), body: {} };
  }

  public getHttpOptions() {
    return this.newOptions;
  }

  private setParams(parameters?: HTTPParams): void {
    let params = new HttpParams();

    if (parameters) {
      Object.keys(parameters).forEach((key) => {
        if (parameters[key]) {
          params = params.set(key, parameters[key]);
        }
      });
    }

    this.newOptions.params = params;
  }
}
